'use es6';

import styled from 'foundations-theming/utils';
import { Fragment } from 'react';
import UniversalSidebarCard from './UniversalSidebarCard';
import { DragHandle } from 'foundations-components/transitional/components/DragHandle';
import { getEmptyImage } from 'react-dnd-compat';
import memoize from 'transmute/memoize';
import identity from 'transmute/identity';
import DnDListWrapper from 'customer-data-ui-utilities/dnd/DnDListWrapper';
import PropTypes from 'prop-types';
import useComponentDidMount from './hooks/useComponentDidMount';
import { jsx as _jsx } from "react/jsx-runtime";
const ExpandableHeader = styled.div.withConfig({
  displayName: "UniversalSidebarCardDnDWrapper__ExpandableHeader",
  componentId: "sc-1t6lo0v-0"
})(({
  use,
  isBordered,
  theme
}) => {
  return Object.assign({
    position: 'relative',
    minHeight: '55px',
    display: 'flex',
    justifyContent: 'space-between',
    paddingInlineStart: theme.space['400'],
    paddingInlineEnd: theme.space['500'],
    paddingBlock: theme.space['250']
  }, isBordered && {
    borderBlockStart: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`
  }, use === 'enclosed' && {
    backgroundColor: theme.color['fill-field-default'],
    borderBlockEnd: `${theme.borderWidth['100']} solid ${theme.color['border-core-default']}`,
    marginBlockStart: '-1px'
  });
});
const CardWrapper = styled.section.withConfig({
  displayName: "UniversalSidebarCardDnDWrapper__CardWrapper",
  componentId: "sc-1t6lo0v-1"
})(["border-color:", ";", " ", " ", " ", ""], props => props.draggingInFront || props.draggingBehind ? props.theme.color['fill-accent-blue-default'] : props.theme.color['fill-field-default'], props => props.isDragging && `background-color: ${props.theme.color['fill-tertiary-hover']};`, props => (props.draggingInFront || props.draggingBehind) && `border-style: solid;`, props => props.draggingInFront && `border-width: ${props.theme.space['50']} ${props.theme.space['0']} ${props.theme.space['0']} ${props.theme.space['0']};`, props => props.draggingBehind && `border-width: ${props.theme.space['0']} ${props.theme.space['0']} ${props.theme.space['50']} ${props.theme.space['0']};`);
const getConnectedDragHandle = memoize(connectDragSource => () => connectDragSource( /*#__PURE__*/_jsx("div", {
  "data-test-id": "universal-sidebar-card-drag-handle",
  children: /*#__PURE__*/_jsx(DragHandle, {})
})));
const getConnectedCardHeader = memoize((connectDragSource, isBordered) => ({
  use,
  children
}) => {
  return connectDragSource( /*#__PURE__*/_jsx("div", {
    "data-test-id": "universal-sidebar-draggable-header",
    children: /*#__PURE__*/_jsx(ExpandableHeader, {
      use: use,
      isBordered: isBordered,
      children: children
    })
  }));
});
const UniversalSidebarCardDnDWrapper = ({
  isBordered,
  // UniversalSidebarCard props
  Actions,
  Content,
  cardProps,
  data,
  draggable,
  isDragging,
  objectId,
  objectType,
  Title,
  // drag and drop props
  connectDragPreview,
  connectDragSource,
  connectDropTarget,
  draggingFromIndex,
  draggingToIndex,
  index
}) => {
  useComponentDidMount(() => {
    connectDragPreview(getEmptyImage(), {
      captureDraggingState: true
    });
  });
  if (!draggable) {
    return /*#__PURE__*/_jsx(UniversalSidebarCard, {
      objectType: objectType,
      objectId: objectId,
      Title: Title,
      Actions: Actions,
      Content: Content,
      cardProps: cardProps,
      data: data
      /* @bend-expect trellis-tokenized */,
      Header: ExpandableHeader,
      isDragging: false,
      draggable: false
    });
  }
  const isCurrentDropTarget = draggingToIndex === index;
  return /*#__PURE__*/_jsx(Fragment, {
    children: connectDropTarget( /*#__PURE__*/_jsx("div", {
      "data-test-id": "universal-sidebar-card-dnd-wrapper",
      children: /*#__PURE__*/_jsx(CardWrapper, {
        isDragging: isDragging,
        draggingInFront: isCurrentDropTarget && draggingToIndex < draggingFromIndex,
        draggingBehind: isCurrentDropTarget && draggingToIndex > draggingFromIndex,
        children: /*#__PURE__*/_jsx(UniversalSidebarCard, {
          objectType: objectType,
          objectId: objectId,
          Title: Title,
          Actions: Actions,
          Content: Content,
          cardProps: cardProps,
          data: data,
          DragHandle: getConnectedDragHandle(connectDragSource),
          Header: getConnectedCardHeader(connectDragSource, isBordered),
          isDragging: isDragging,
          draggable: draggable
        })
      })
    }))
  });
};
UniversalSidebarCardDnDWrapper.propTypes = {
  Content: PropTypes.func.isRequired,
  Title: PropTypes.func.isRequired,
  Actions: PropTypes.func,
  cardProps: PropTypes.object,
  data: PropTypes.object.isRequired,
  objectType: PropTypes.string.isRequired,
  objectId: PropTypes.string.isRequired,
  isDragging: PropTypes.bool,
  isBordered: PropTypes.bool,
  draggable: PropTypes.bool,
  connectDragSource: PropTypes.func.isRequired,
  connectDropTarget: PropTypes.func.isRequired,
  connectDragPreview: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  draggingFromIndex: PropTypes.number,
  draggingToIndex: PropTypes.number
};

// for clarity, the DnDListWrapper is meant to wrap individual items in a drag and drop list (like a card), not the list itself
const makeUniversalSidebarCardDnDWrapper = memoize(namespace => {
  const wrapper = DnDListWrapper(namespace, identity)(UniversalSidebarCardDnDWrapper);
  wrapper.displayName = `UniversalSidebarCardDnDWrapper(${namespace})`;
  return wrapper;
});
export { UniversalSidebarCardDnDWrapper, makeUniversalSidebarCardDnDWrapper };